"use client";

/**
 * "Market pulse" panel for the trends hub: the city-level weekly AZN/m²
 * series, momentum chips, the new-vs-old building spread, and the
 * district movers.
 *
 * Deliberately NO forward projection: the API's Theil-Sen trend
 * extension exists (and will feed a real model once enough weekly
 * history accumulates), but with single-digit basis weeks we don't put
 * an extrapolated line in front of users - real observed data only.
 */
import { signedPct } from "@/lib/format/change";
import { useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import WhenVisible from "@/components/WhenVisible";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { useT, useLocale } from "@/lib/locale-context";
import { useTheme } from "@/lib/theme-context";
import { fetchAPI } from "@/lib/api";
import { formatMonthDayShort } from "@/lib/blog-format";

const Chart = dynamic(() => import("react-apexcharts"), { ssr: false });

interface Outlook {
  basis_weeks: number;
  series: { week: string; ppm: number }[];
  median_ppm_now: number;
  momentum_4w_pct: number | null;
  momentum_8w_pct: number | null;
  projection: { step: number; ppm: number; low: number; high: number }[];
  segments: Record<string, { n: number; median_ppm: number | null }>;
  risers: { district: string; change_4w_pct: number; median_ppm: number }[];
  fallers: { district: string; change_4w_pct: number; median_ppm: number }[];
}

function fmt(n: number | null | undefined): string {
  if (!n) return "-";
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/** "2026-W15" -> ms of that ISO week's Monday (UTC). */
function weekToMs(weekKey: string): number {
  const m = /^(\d{4})-W(\d{2})$/.exec(weekKey);
  if (!m) return Date.now();
  const year = parseInt(m[1], 10);
  const week = parseInt(m[2], 10);
  const jan4 = Date.UTC(year, 0, 4);
  const jan4Dow = new Date(jan4).getUTCDay() || 7;
  return jan4 - (jan4Dow - 1) * 86400000 + (week - 1) * 7 * 86400000;
}

function MomentumChip({ pct, label }: { pct: number | null; label: string }) {
  if (pct == null) return null;
  const up = pct > 0.2;
  const down = pct < -0.2;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold tabular-nums ${
      up ? "bg-emerald-50 text-emerald-700"
        : down ? "bg-rose-50 text-rose-700" : "bg-gray-100 text-gray-600"
    }`}>
      {up ? <ArrowUpRight className="w-3 h-3" />
        : down ? <ArrowDownRight className="w-3 h-3" /> : null}
      {signedPct(pct, 1)} · {label}
    </span>
  );
}

export default function MarketOutlook({ mode = "sale" }: { mode?: "sale" | "rent" }) {
  const t = useT();
  const { locale } = useLocale();
  const { resolved } = useTheme();
  const isDark = resolved === "dark";
  const [data, setData] = useState<Outlook | null>(null);
  // Distinguish "still fetching" (skeleton holds the slot) from "no
  // outlook exists" (render nothing): returning null while loading
  // made the whole pulse section POP IN and shove the page down.
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    let alive = true;
    setSettled(false);
    fetchAPI<Outlook>("/trends/outlook", { mode })
      .then((d) => { if (alive) setData(d); })
      .catch(() => { /* no outlook yet - render nothing */ })
      .finally(() => { if (alive) setSettled(true); });
    return () => { alive = false; };
  }, [mode]);

  const chart = useMemo(() => {
    if (!data) return null;
    const points = data.series.map((p) => ({ x: weekToMs(p.week), y: p.ppm }));
    const labelColor = isDark ? "#9ca3af" : "#6b7280";
    return {
      series: [{ name: "AZN/m²", data: points }],
      options: {
        chart: {
          type: "area" as const,
          toolbar: { show: false },
          dropShadow: { enabled: true, color: "#6366f1", blur: 8, opacity: isDark ? 0.4 : 0.25, top: 0, left: 0 },
          zoom: { enabled: false },
          animations: { enabled: false },
          background: "transparent",
          parentHeightOffset: 0,
        },
        stroke: { curve: "smooth" as const, width: 2.5 },
        colors: ["#6366f1"],
        fill: {
          type: "gradient",
          gradient: { opacityFrom: 0.18, opacityTo: 0.02 },
        },
        dataLabels: { enabled: false },
        xaxis: {
          type: "datetime" as const,
          labels: {
            style: { fontSize: "10px", colors: labelColor },
            datetimeUTC: true,
            formatter: (value: string) => formatMonthDayShort(Number(value), locale),
            hideOverlappingLabels: true,
          },
          tickAmount: 5,
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { fontSize: "10px", colors: labelColor },
            formatter: (v: number) => `${Math.round(v)}`,
          },
        },
        grid: { borderColor: isDark ? "#374151" : "#f3f4f6", strokeDashArray: 4 },
        tooltip: {
          theme: isDark ? "dark" : "light",
          y: { formatter: (v: number) => `${Math.round(v)} AZN/m²` },
        },
      },
    };
  }, [data, isDark, locale]);

  if (!settled && !data) {
    // Shape-matched placeholder (header line, chip row, chart block)
    // so the section's slot exists from first paint.
    return (
      <section className="mt-8 rounded-2xl border border-gray-200 bg-white p-5">
        <div className="h-4 w-44 rounded bg-gray-200 animate-pulse" />
        <div className="mt-3 flex gap-2">
          <div className="h-6 w-24 rounded-full bg-gray-100 animate-pulse" />
          <div className="h-6 w-24 rounded-full bg-gray-100 animate-pulse" />
        </div>
        <div className="mt-4 h-48 rounded-xl bg-gray-50 animate-pulse" />
      </section>
    );
  }
  if (!data || !chart) return null;

  const fill = (s: string, map: Record<string, string | number>) =>
    Object.entries(map).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(String(v)), s);
  const newSeg = data.segments.new;
  const oldSeg = data.segments.old;

  return (
    <section className="card-enter mt-8 rounded-2xl border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-bold text-gray-900">{t("outlook.title")}</h2>
        <div className="flex flex-wrap gap-1.5">
          <MomentumChip pct={data.momentum_4w_pct} label={t("outlook.momentum4w")} />
          <MomentumChip pct={data.momentum_8w_pct} label={t("outlook.momentum8w")} />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-2">
        <div>
          <div className="text-3xl font-extrabold text-gray-900 tabular-nums leading-none">
            {fmt(data.median_ppm_now)}
            <span className="ml-1 text-sm font-semibold text-gray-500">AZN/m²</span>
          </div>
          <div className="mt-1 text-[11px] text-gray-500">{t("outlook.now")}</div>
        </div>
        {(newSeg?.median_ppm || oldSeg?.median_ppm) && (
          <div className="flex gap-3 text-[11px] text-gray-600">
            {newSeg?.median_ppm && (
              <span>
                {t("outlook.newb")}{" "}
                <strong className="text-gray-900 tabular-nums">{fmt(newSeg.median_ppm)}</strong> AZN/m²
              </span>
            )}
            {oldSeg?.median_ppm && (
              <span>
                {t("outlook.oldb")}{" "}
                <strong className="text-gray-900 tabular-nums">{fmt(oldSeg.median_ppm)}</strong> AZN/m²
              </span>
            )}
          </div>
        )}
      </div>

      <div className="mt-2 -mx-2">
        <WhenVisible className="h-[170px]">
          <Chart type="area" height={170} series={chart.series} options={chart.options} />
        </WhenVisible>
      </div>

      {(data.risers.length > 0 || data.fallers.length > 0) && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[12px]">
          {data.risers.length > 0 && (
            <div>
              <div className="text-[10px] text-gray-400 mb-1">
                {t("outlook.risers")}
              </div>
              {data.risers.map((r) => (
                <div key={r.district} className="flex items-center justify-between py-0.5">
                  <span className="text-gray-700">{r.district}</span>
                  <span className="font-semibold text-emerald-600 tabular-nums">
                    {signedPct(r.change_4w_pct, 1)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {data.fallers.length > 0 && (
            <div>
              <div className="text-[10px] text-gray-400 mb-1">
                {t("outlook.fallers")}
              </div>
              {data.fallers.map((r) => (
                <div key={r.district} className="flex items-center justify-between py-0.5">
                  <span className="text-gray-700">{r.district}</span>
                  <span className="font-semibold text-rose-600 tabular-nums">
                    {signedPct(r.change_4w_pct, 1)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <p className="mt-3 text-[10px] text-gray-400">
        {fill(t("outlook.disclaimer"), { n: data.basis_weeks })}
      </p>
    </section>
  );
}
